import { Card, CardContent, Tooltip, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Query } from '@/components/atoms/query';
import { Dialog, DialogActions } from '@/components/molecules/dialog';
import { useUpdateHostDiscountTemplate } from '@/features/hosts/hooks/mutation/use-update-host-discount-templates';
import { useDiscountTemplates } from '@/features/hosts/hooks/query/use-discount-templates';

interface Props extends DialogActions {
  hostId: number;
  discountTemplatesIds: number[];
  billingGroupDiscountTemplatesIds: number[];
}

export const UpdateHostDiscountsDialog = ({
  hostId,
  onCancel,
  open,
  discountTemplatesIds,
  billingGroupDiscountTemplatesIds,
}: Props) => {
  const { t } = useTranslation(['hosts', 'host', 'common']);

  const [query, setQuery] = useState('');

  const { data } = useDiscountTemplates();

  const [selectedTemplatesIds, setSelectedTemplatesIds] = useState(discountTemplatesIds || []);

  const hasChanges =
    selectedTemplatesIds.length !== discountTemplatesIds.length ||
    selectedTemplatesIds.some(id => !discountTemplatesIds.includes(id));

  const onSuccessUpdateHostDiscountTemplate = () => {
    onCancel?.();
    setSelectedTemplatesIds([]);
  };

  const { mutate } = useUpdateHostDiscountTemplate(onSuccessUpdateHostDiscountTemplate);

  const handleSelectDiscounts = (id: number) => {
    if (billingGroupDiscountTemplatesIds.includes(id)) {
      return;
    }

    const copy = selectedTemplatesIds.slice();

    if (copy.findIndex(el => el === id) > -1) {
      copy.splice(
        copy.findIndex(el => el === id),
        1,
      );
    } else {
      copy.push(id);
    }

    setSelectedTemplatesIds(copy);
  };

  useEffect(() => {
    setSelectedTemplatesIds(discountTemplatesIds);
  }, [discountTemplatesIds]);

  return (
    <Dialog
      onCancel={onCancel}
      open={open}
      title={t('host:discount.chooseDiscounts')}
      variant="info"
      confirmButtonText={t('common:button.confirm')}
      isConfirmButtonDisabled={!hostId || !hasChanges}
      onConfirm={() => mutate({ hostId, discountTemplates: selectedTemplatesIds })}>
      <Query disabled={false} value={query} onChange={value => setQuery(value)} searchAsType />

      <Box sx={{ my: 1 }}>
        {data && data?.templates?.length > 0
          ? data?.templates
              ?.filter(dest => dest.name.toLowerCase().includes(query.toLowerCase()))
              ?.map(destination => {
                const { name, id } = destination;
                const isFromBillingGroup = billingGroupDiscountTemplatesIds.includes(id);
                const isSelected = isFromBillingGroup || selectedTemplatesIds.includes(id);

                return (
                  <Tooltip
                    key={id}
                    title={t('host:discount.fromBillingGroup')}
                    disableHoverListener={!isFromBillingGroup}
                    disableFocusListener={!isFromBillingGroup}>
                    <Card
                      onClick={() => handleSelectDiscounts(id)}
                      sx={{
                        boxShadow: theme =>
                          isSelected
                            ? `0px 0px 0px 2px ${theme.palette.primary.main}`
                            : `0px 0px 0px 1px ${theme.palette.divider}`,
                        cursor: isFromBillingGroup ? 'not-allowed' : 'pointer',
                        backgroundColor: isFromBillingGroup
                          ? 'action.disabledBackground'
                          : undefined,
                        mb: 1,
                      }}>
                      <CardContent
                        sx={{
                          alignItems: 'center',
                          display: 'flex',
                        }}>
                        <Box>
                          <Typography color="textPrimary" variant="h6">
                            {name}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Tooltip>
                );
              })
          : t('hosts:modal.thereIsNoDiscounts')}
      </Box>
    </Dialog>
  );
};
